import * as THREE from 'three';

/**
 * Calculate a point on a bezier curve
 * @param {number} t - The parameter value between 0 and 1
 * @param {THREE.Vector3} p0 - The first control point
 * @param {THREE.Vector3} p1 - The second control point
 * @param {THREE.Vector3} p2 - The third control point
 * @param {THREE.Vector3} p3 - The fourth control point
 * @returns {THREE.Vector3} - The point on the curve
 */
export const calculateBezierPoint = (t, p0, p1, p2, p3) => {
  try {
    // Validate inputs
    if (t < 0 || t > 1) {
      console.warn('Bezier parameter t should be between 0 and 1:', t);
      t = Math.max(0, Math.min(1, t));
    }
    
    if (!p0 || !p1 || !p2 || !p3) {
      throw new Error('Invalid bezier control points provided');
    }

    const u = 1 - t;
    const tt = t * t;
    const uu = u * u;
    const uuu = uu * u;
    const ttt = tt * t;
    
    const result = new THREE.Vector3();
    result.multiplyVectors(p0, new THREE.Vector3(uuu, uuu, uuu));
    result.addScaledVector(p1, 3 * uu * t);
    result.addScaledVector(p2, 3 * u * tt);
    result.addScaledVector(p3, ttt);
    
    return result;
  } catch (error) {
    console.error('Error calculating bezier point:', error);
    return new THREE.Vector3(); // Return origin as fallback
  }
};

/**
 * Generate curve points from bezier handles and points
 * @param {THREE.Vector3[]} points - The points of the curve
 * @param {THREE.Vector3[]} handles - The handles of the curve
 * @param {number} resolution - The resolution of the curve
 * @returns {Float32Array} - The curve points
 */
export const generateCurvePoints = (points, handles, resolution = 100) => {
  const MIN_RESOLUTION = 10;
  const MAX_RESOLUTION = 5000;
  try {
    // Validate inputs
    if (!points || !Array.isArray(points) || points.length < 3) {
      throw new Error('Invalid points array: need at least 3 points');
    }
    
    if (!handles || !Array.isArray(handles) || handles.length !== points.length) {
      throw new Error('Handles array must match points array length');
    }

    if (resolution < MIN_RESOLUTION || resolution > MAX_RESOLUTION) {
      console.warn('Resolution should be between 10-1000:', resolution);
      resolution = Math.max(MIN_RESOLUTION, Math.min(MAX_RESOLUTION, resolution));
    }

    const curvePoints = [];
    const numPoints = points.length;
    
    for (let i = 0; i < numPoints; i++) {
      try {
        const current = new THREE.Vector3(points[i].x, points[i].y, points[i].z);
        const next = new THREE.Vector3(points[(i + 1) % numPoints].x, points[(i + 1) % numPoints].y, points[(i + 1) % numPoints].z);
        
        const currentOut = new THREE.Vector3(handles[i].out.x, handles[i].out.y, handles[i].out.z).add(current);
        const nextIn = new THREE.Vector3(handles[(i + 1) % numPoints].in.x, handles[(i + 1) % numPoints].in.y, handles[(i + 1) % numPoints].in.z).add(next);
        
        for (let t = 0; t < 1; t += 1 / resolution) {
          const point = calculateBezierPoint(t, current, currentOut, nextIn, next);
          if (point && isFinite(point.x) && isFinite(point.y) && isFinite(point.z)) {
            curvePoints.push(point.x, point.y, point.z);
          }
        }
      } catch (segmentError) {
        console.error(`Error processing curve segment ${i}:`, segmentError);
      }
    }
    
    if (curvePoints.length === 0) {
      throw new Error('No valid curve points generated');
    }
    
    return new Float32Array(curvePoints);
  } catch (error) {
    console.error('Error generating curve points:', error);
    // Return a simple fallback curve (straight line)
    const fallbackPoints = [0, 0, 0, 1, 0, 0];
    return new Float32Array(fallbackPoints);
  }
};

/**
 * Project curve points onto a mesh
 * @param {Float32Array} curvePositions - The curve positions
 * @param {THREE.Mesh} mesh - The mesh to project onto
 * @param {Object} bvh - The bounding volume hierarchy of the mesh
 * @returns {Float32Array} - The projected curve positions
 */
export const projectCurvePointsOntoMesh = (curvePositions, mesh, bvh) => {
  try {
    if (!mesh || !bvh || !curvePositions || curvePositions.length === 0) return curvePositions;

    const projected = new Float32Array(curvePositions.length);
    const { center, size } = getMeshWorldBounds(mesh);

    // Choose a push distance guaranteed to be outside the mesh in XZ
    const pushDistance = Math.max(size.x, size.z) * 1.25;

    // Prepare transforms for BVH local raycast
    const worldToLocal = new THREE.Matrix4().copy(mesh.matrixWorld).invert();
    const localToWorld = mesh.matrixWorld;

    // Reusable objects to avoid GC
    const tmpOrigin = new THREE.Vector3();
    const tmpDir = new THREE.Vector3();
    const tmpLocalRay = new THREE.Ray();
    const tmpWorldPoint = new THREE.Vector3();

    for (let i = 0; i < curvePositions.length; i += 3) {
      const px = curvePositions[i];
      const py = curvePositions[i + 1];
      const pz = curvePositions[i + 2];

      // Outward direction on XZ plane from mesh center
      tmpDir.set(px - center.x, 0, pz - center.z);
      const len = tmpDir.length();
      if (len < 1e-5) {
        // Degenerate: pick arbitrary outward direction
        tmpDir.set(1, 0, 0);
      } else {
        tmpDir.multiplyScalar(1 / len);
      }

      // Start from outside point and cast back inward
      tmpOrigin.set(px + tmpDir.x * pushDistance, py, pz + tmpDir.z * pushDistance);
      const inwardDir = tmpDir.clone().multiplyScalar(-1);

      // Build local-space ray
      tmpLocalRay.origin.copy(tmpOrigin).applyMatrix4(worldToLocal);
      tmpLocalRay.direction.copy(inwardDir).transformDirection(worldToLocal).normalize();

      const hit = bvh.raycastFirst(tmpLocalRay);
      if (hit && hit.point) {
        tmpWorldPoint.copy(hit.point).applyMatrix4(localToWorld);
        projected[i] = tmpWorldPoint.x;
        projected[i + 1] = tmpWorldPoint.y;
        projected[i + 2] = tmpWorldPoint.z;
        continue;
      }

      // Fallback: cast downward from above bbox top
      tmpOrigin.set(px, center.y + size.y * 1.5, pz);
      tmpLocalRay.origin.copy(tmpOrigin).applyMatrix4(worldToLocal);
      tmpLocalRay.direction.set(0, -1, 0).transformDirection(worldToLocal).normalize();
      const downHit = bvh.raycastFirst(tmpLocalRay);
      if (downHit && downHit.point) {
        tmpWorldPoint.copy(downHit.point).applyMatrix4(localToWorld);
        projected[i] = tmpWorldPoint.x;
        projected[i + 1] = tmpWorldPoint.y;
        projected[i + 2] = tmpWorldPoint.z;
        continue;
      }

      // As last resort, keep original point
      projected[i] = px;
      projected[i + 1] = py;
      projected[i + 2] = pz;
    }

    return projected;
  } catch (error) {
    console.error('Error projecting curve points onto mesh:', error);
    return curvePositions;
  }
};

/**
 * Project a point onto a mesh
 * @param {THREE.Vector3} point - The point to project
 * @param {THREE.Vector3} targetCenter - The center of the mesh
 * @param {Object} bvh - The bounding volume hierarchy of the mesh
 * @returns {THREE.Vector3} - The projected point
 */
export const projectPointOntoMesh = (point, targetCenter, bvh, mesh) => {
  if (!mesh || !bvh) return point;

  // BVH operates in local (geometry) space, so we need to transform rays accordingly
  const worldToLocal = new THREE.Matrix4().copy(mesh.matrixWorld).invert();
  const localToWorld = mesh.matrixWorld;

  const origin = new THREE.Vector3(point.x, point.y, point.z);

  // The most reliable direction is from the point towards the center of the mesh
  // Project only in the XZ plane (ignore Y values)
  const flatOrigin = origin.clone(); flatOrigin.y = 0;
  const flatTarget = targetCenter.clone(); flatTarget.y = 0;
  const direction = new THREE.Vector3().subVectors(flatTarget, flatOrigin).normalize();

  // Transform ray to local space
  const localRay = new THREE.Ray();
  localRay.origin.copy(origin).applyMatrix4(worldToLocal);
  localRay.direction.copy(direction).transformDirection(worldToLocal).normalize();

  // Use BVH-accelerated raycasting in local space
  const intersects = bvh.raycastFirst(localRay);

  if (intersects) {
    // Transform hit point back to world space
    const worldPoint = new THREE.Vector3().copy(intersects.point).applyMatrix4(localToWorld);
    return { x: worldPoint.x, y: worldPoint.y, z: worldPoint.z };
  }

  // Fallback if the primary raycast fails (should be rare)
  console.warn('Could not project point towards center, using downward projection as fallback.');
  localRay.origin.copy(origin).applyMatrix4(worldToLocal);
  localRay.direction.set(0, -1, 0).transformDirection(worldToLocal).normalize();

  const fallbackIntersects = bvh.raycastFirst(localRay);
  if (fallbackIntersects) {
      const worldPoint = new THREE.Vector3().copy(fallbackIntersects.point).applyMatrix4(localToWorld);
      return { x: worldPoint.x, y: worldPoint.y, z: worldPoint.z };
  }

  console.error('Could not project point onto surface at all, using original position:', point);
  return point;
};

/**
 * Get the surface normal at a point on a mesh
 * @param {THREE.Vector3} point - The point to get the normal at
 * @param {THREE.Mesh} mesh - The mesh to get the normal from
 * @param {Object} bvh - The bounding volume hierarchy of the mesh
 * @returns {THREE.Vector3} - The surface normal
 */
export const getSurfaceNormalAtPoint = (point, mesh, bvh) => {
  if (!mesh || !bvh) return new THREE.Vector3(0, 1, 0); // Default up vector

  try {
    // BVH operates in local space, so transform rays accordingly
    const worldToLocal = new THREE.Matrix4().copy(mesh.matrixWorld).invert();
    const localToWorld = mesh.matrixWorld;

    const origin = new THREE.Vector3(point.x, point.y, point.z);

    // Cast rays from multiple directions to find the closest surface point
    const directions = [
      new THREE.Vector3(0, 1, 0),   // Up
      new THREE.Vector3(0, -1, 0),  // Down
      new THREE.Vector3(1, 0, 0),   // Right
      new THREE.Vector3(-1, 0, 0),  // Left
      new THREE.Vector3(0, 0, 1),   // Forward
      new THREE.Vector3(0, 0, -1),  // Back
    ];

    let closestHit = null;
    let closestDistance = Infinity;

    for (const direction of directions) {
      // Offset the origin slightly in the opposite direction
      const offsetOrigin = origin.clone().add(direction.clone().multiplyScalar(-0.1));

      // Transform to local space for BVH raycast
      const localRay = new THREE.Ray();
      localRay.origin.copy(offsetOrigin).applyMatrix4(worldToLocal);
      localRay.direction.copy(direction).transformDirection(worldToLocal).normalize();

      const hit = bvh.raycastFirst(localRay);
      if (hit && hit.face) {
        const distance = hit.distance;
        if (distance < closestDistance) {
          closestDistance = distance;
          closestHit = hit;
        }
      }
    }
    
    if (closestHit && closestHit.face) {
      // Get the face normal in world space
      const face = closestHit.face;
      const geometry = mesh.geometry;
      const positions = geometry.attributes.position;

      // Get face vertices
      const a = new THREE.Vector3().fromBufferAttribute(positions, face.a);
      const b = new THREE.Vector3().fromBufferAttribute(positions, face.b);
      const c = new THREE.Vector3().fromBufferAttribute(positions, face.c);

      // Transform to world space
      a.applyMatrix4(mesh.matrixWorld);
      b.applyMatrix4(mesh.matrixWorld);
      c.applyMatrix4(mesh.matrixWorld);

      // Calculate face normal
      const normal = new THREE.Vector3();
      const cb = c.clone().sub(b);
      const ab = a.clone().sub(b);
      normal.crossVectors(cb, ab).normalize();

      // Ensure normal points outward (away from mesh center)
      const meshCenter = new THREE.Box3().setFromObject(mesh).getCenter(new THREE.Vector3());
      // Transform hit point to world space for comparison
      const worldHitPoint = new THREE.Vector3().copy(closestHit.point).applyMatrix4(localToWorld);
      const toCenter = meshCenter.clone().sub(worldHitPoint);
      if (normal.dot(toCenter) > 0) {
        normal.negate();
      }

      return normal;
    }
    
    // Fallback: use the direction from mesh center to point as approximate normal
    const meshCenter = new THREE.Box3().setFromObject(mesh).getCenter(new THREE.Vector3());
    const normal = new THREE.Vector3().subVectors(origin, meshCenter).normalize();
    return normal;
    
  } catch (error) {
    console.error('Error calculating surface normal:', error);
    return new THREE.Vector3(0, 1, 0); // Default up vector
  }
};

/**
 * Convert world positions float32array to THREE.Vector3 objects
 * @param {Float32Array} worldPositions - World positions
 * @returns {THREE.Vector3[]} - Points in world space
 */
export function convertToWorldPositions(worldPositions) {
    const pointsWorld = [];
    for (let i = 0; i < worldPositions.length; i += 3) {
      const x = worldPositions[i];
      const y = worldPositions[i + 1];
      const z = worldPositions[i + 2];
      if (isFinite(x) && isFinite(y) && isFinite(z)) {
        pointsWorld.push(new THREE.Vector3(x, y, z));
      }
    }
    return pointsWorld;
}

/**
 * Get the world bounds of a mesh
 * @param {THREE.Mesh} mesh - The mesh to get the bounds from
 * @returns {Object} - The center and size of the mesh
 */
export const getMeshWorldBounds = (mesh) => {
  const bbox = (mesh.geometry.boundingBox ? mesh.geometry.boundingBox.clone() : (mesh.geometry.computeBoundingBox(), mesh.geometry.boundingBox.clone()));
  bbox.applyMatrix4(mesh.matrixWorld);
  const center = bbox.getCenter(new THREE.Vector3());
  const size = bbox.getSize(new THREE.Vector3());
  return { center, size };
};